import { Skeleton, vars } from '@etg/wings';
import { useRevolut, useTranslation } from '@eti/providers';
// oxlint-disable-next-line no-restricted-imports -- TODO WEBTECH-1501: Replace old schema object types with operation-specific types.
import type { BestPricePerDay } from '@eti/schema-types/schema-objects';
import { css, cx } from '@eti/styles';
import { isBefore, isSameDay, parseISO } from 'date-fns';
import type { ButtonHTMLAttributes, ReactElement } from 'react';
import { useEffect, useRef } from 'react';
import type { Modifiers } from 'react-day-picker';

const priceCategories: Record<
  string,
  { ariaLabel: string; priceLabel: string | ReactElement; backgroundColor: string; color: string }
> = {
  LOW: {
    ariaLabel: 'ScreenReader.Calendar.Day.Price.Low.AriaLabel',
    priceLabel: '$',
    backgroundColor: vars.colors.calendar.price.low.background,
    color: vars.colors.calendar.price.low.foreground,
  },
  HIGH: {
    ariaLabel: 'ScreenReader.Calendar.Day.Price.High.AriaLabel',
    priceLabel: '$$$',
    backgroundColor: vars.colors.calendar.price.high.background,
    color: vars.colors.calendar.price.high.foreground,
  },
  MEDIUM: {
    ariaLabel: 'ScreenReader.Calendar.Day.Price.Medium.AriaLabel',
    priceLabel: '$$',
    backgroundColor: vars.colors.calendar.price.medium.background,
    color: vars.colors.calendar.price.medium.foreground,
  },
  NOT_AVAILABLE: {
    ariaLabel: 'ScreenReader.Calendar.Day.Price.Medium.AriaLabel',
    priceLabel: '···',
    backgroundColor: vars.colors.calendar.price.unknown.background,
    color: vars.colors.calendar.price.unknown.foreground,
  },
};

const getPriceStyle = (isRevolut: boolean, category?: string) => css`
  ${category ? `background-color: ${priceCategories[category].backgroundColor}` : ''};
  align-items: center;
  border-radius: ${isRevolut ? vars.card.borderRadius : '2px'};
  color: ${category ? priceCategories[category].color : ''};
  display: flex;
  font-size: 0.6rem;
  height: ${isRevolut ? '20px' : '16px'};
  justify-content: center;
  padding: 1px 0;
  text-align: center;
  width: ${isRevolut ? '30px' : '100%'};
`;

const containerStyles = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
  position: relative;
  width: 100%;
`;

const priceIndicatorPaddingStyles = css`
  padding: 2px 6px 6px;
`;

const priceIndicatorRevolutPaddingStyles = css`
  padding: 4px 6px 0;
`;

const dayNumberStyles = css`
  align-items: center;
  display: flex;
  height: 1.5rem;
  justify-content: center;
  text-align: center;
  width: 1.5rem;
`;

export interface PriceCalendarProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  date: Date;
  modifiers: Modifiers;
  priceCalendarData?: BestPricePerDay[];
  loading: boolean;
}

const CustomDayWithPriceIndicator = ({
  date,
  priceCalendarData,
  modifiers,
  loading,
  ...buttonProps
}: PriceCalendarProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);
  const { isRevolut } = useRevolut();

  const priceData = priceCalendarData?.find((d) => isSameDay(parseISO(d.date), date));
  const lastDayOfPriceData = priceCalendarData?.[priceCalendarData?.length - 1];
  const shouldShowPriceIndicator =
    !loading &&
    !modifiers.disabled &&
    Boolean(lastDayOfPriceData?.date && !isBefore(new Date(lastDayOfPriceData.date), date));
  const { className, ...otherButtonProps } = buttonProps;
  const currentPriceCategory = priceCategories[priceData?.priceCategory ?? 'NOT_AVAILABLE'];

  useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  return (
    <button
      className={cx(className, containerStyles, {
        [priceIndicatorRevolutPaddingStyles]: isRevolut,
        [priceIndicatorPaddingStyles]: !isRevolut,
      })}
      ref={ref}
      type="button"
      {...otherButtonProps}
      aria-label={`${otherButtonProps['aria-label']} ${t(currentPriceCategory.ariaLabel)}`}
    >
      <div className={dayNumberStyles}>{date.getDate()}</div>
      {loading && (
        <Skeleton
          className={getPriceStyle(isRevolut)}
          data-testid="price-calendar-loading-indicator"
        />
      )}
      {shouldShowPriceIndicator && (
        <div
          className={getPriceStyle(isRevolut, priceData?.priceCategory ?? 'NOT_AVAILABLE')}
          data-testid="price-calendar-indicator"
        >
          {currentPriceCategory.priceLabel}
        </div>
      )}
      {!shouldShowPriceIndicator && !loading && (
        <div
          className={getPriceStyle(isRevolut)}
          data-testid="price-calendar-indicator-placeholder"
        />
      )}
    </button>
  );
};

export default CustomDayWithPriceIndicator;
